import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Mail, Clock, CheckCircle, AlertCircle, PenTool, TrendingUp, Send } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import { site } from '@/data/content';

const contentGuidelines = [
  { icon: 'Target', title: 'Industry Relevance', text: 'Topics must focus on Customer Experience (CX) and Customer Support industry trends, strategies, tools, and best practices.' },
  { icon: 'FileText', title: 'Word Count', text: 'Articles should be between 1,000 to 10,000 words. Long-form content that provides actionable insights is preferred.' },
  { icon: 'PenTool', title: 'Original Content', text: 'All submissions must be original, unpublished work. We do not accept content that has been published elsewhere.' },
  { icon: 'Users', title: 'Audience Value', text: 'Write for CX professionals, support managers, and business leaders. Focus on practical, actionable advice.' },
  { icon: 'Lightbulb', title: 'Data-Driven', text: 'Include statistics, case studies, real-world examples, and actionable takeaways to strengthen your article.' },
  { icon: 'CheckCircle', title: 'Quality Standards', text: 'Content must be well-researched, factually accurate, and free from grammatical errors.' },
];

const acceptedFormats = [
  { icon: 'FileText', title: 'Written Articles', text: 'Document files (.doc, .docx), PDF, Google Docs, or any readable text format.', formats: 'DOC, DOCX, PDF, Google Docs' },
  { icon: 'Video', title: 'Video Ads', text: 'Short-form video content for brand awareness and product education.', formats: 'MP4, MOV, WebM (up to 5 minutes)' },
  { icon: 'Mic', title: 'Audio Ads', text: 'Podcast-style content or audio advertisements for voice platforms.', formats: 'MP3, WAV (up to 3 minutes)' },
];

const topicCategories = [
  { icon: 'MessageCircle', title: 'Live Chat Strategies', examples: ['Chat widget optimization', 'Response time best practices', 'Chat agent training'] },
  { icon: 'FileText', title: 'Ticket Management', examples: ['Workflow automation', 'Priority routing', 'SLA management'] },
  { icon: 'BookOpen', title: 'Knowledge Base', examples: ['Documentation best practices', 'Self-service strategies', 'Content organization'] },
  { icon: 'Sparkles', title: 'AI in Customer Support', examples: ['AI chatbots', 'Automated responses', 'Sentiment analysis'] },
  { icon: 'Phone', title: 'Voice Support', examples: ['Call center strategies', 'IVR optimization', 'Voice AI integration'] },
  { icon: 'Zap', title: 'Omnichannel Support', examples: ['Channel integration', 'Unified inbox', 'Cross-channel consistency'] },
  { icon: 'TrendingUp', title: 'Analytics & Metrics', examples: ['CSAT improvement', 'Response time tracking', 'Performance KPIs'] },
  { icon: 'Users', title: 'Team Management', examples: ['Agent training', 'Shift scheduling', 'Quality assurance'] },
];

const reviewProcess = [
  { step: '1', title: 'Submission', text: 'Send your content to marketing@getzurihub.com with subject line "Content Submission: [Your Title]"' },
  { step: '2', title: 'Initial Review', text: 'Our team reviews your submission for relevance, quality, and alignment with our editorial standards.' },
  { step: '3', title: 'Quality Assessment', text: 'Content is evaluated for depth, originality, practical value, and industry expertise.' },
  { step: '4', title: 'Editorial Decision', text: 'Accepted submissions are scheduled for publication. Authors are notified within 30 days.' },
  { step: '5', title: 'Publication', text: 'Published content is promoted across our channels, and authors receive their chosen reward.' },
];

const rewards = [
  { icon: 'Award', title: '2-Year Pro Subscription', text: 'Full access to all ZuriHub Pro features for 24 months. Unlimited agents, all channels, AI replies, and advanced analytics.', value: 'Premium' },
  { icon: 'TrendingUp', title: '$100 - $1,000 Cash', text: 'Cash compensation based on content quality, depth, and impact. Higher rewards for exceptional, data-rich content.', value: 'Flexible' },
];

const seoTips = [
  { title: 'Keyword Research', text: 'Include relevant CX and customer support keywords naturally throughout your content.' },
  { title: 'Answer Engine Optimization', text: 'Structure content to answer common questions clearly and concisely for AI-powered search engines.' },
  { title: 'Featured Snippets', text: 'Use clear headings, bullet points, and concise answers to increase chances of appearing in search results.' },
  { title: 'Generative Engine Optimization', text: 'Create authoritative, well-sourced content that AI models trust and reference.' },
];

export default function WriteForUs() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `Write for Us - ${site.name}`,
    description: `Submit guest posts and content about Customer Experience (CX) and Customer Support industry to ${site.name}. Get rewarded with Pro subscription or cash payments.`,
    url: `${site.baseUrl}/write-for-us`,
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: site.baseUrl,
    logo: `${site.baseUrl}/og-image.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'marketing@getzurihub.com',
      contactType: 'content submissions',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What topics do you accept for guest posts?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We accept content related to Customer Experience (CX) and Customer Support industry, including live chat strategies, ticket management, knowledge base best practices, AI in support, voice support, omnichannel strategies, analytics, and team management.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the word count requirement for articles?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Articles should be between 1,000 to 10,000 words. We prefer long-form, comprehensive content that provides actionable insights and in-depth analysis.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does the review process take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our review process takes up to 30 days. Accepted submissions will hear from our content marketing team within this timeframe.',
        },
      },
      {
        '@type': 'Question',
        name: 'What rewards do you offer for accepted content?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can choose between a 2-year free Pro subscription or a cash payment between $100 to $1,000, depending on the quality and impact of your work.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do you accept video and audio content?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we accept video ads (up to 5 minutes in MP4, MOV, or WebM format) and audio ads (up to 3 minutes in MP3 or WAV format) for brand awareness and product education.',
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="Write for Us - Submit CX & Customer Support Content"
        description="Share your expertise in Customer Experience and Customer Support. Submit guest posts, articles, video ads, and audio content. Get rewarded with Pro subscription or cash payments up to $1,000."
        path="/write-for-us"
        jsonLd={[jsonLd, organizationJsonLd, faqJsonLd]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-brand-500/10 px-4 py-2 text-sm font-semibold text-brand-300">
            <PenTool className="h-4 w-4" />
            Content Contribution Program
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl">
            Share Your <span className="gradient-text">CX Expertise</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Contribute industry insights, case studies, and best practices to help businesses transform their customer experience. Get rewarded for your expertise.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="mailto:marketing@getzurihub.com?subject=Content%20Submission%3A%20%5BYour%20Title%5D"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              <Mail className="h-4 w-4" />
              Submit Your Content <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/road-map"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              View Our Roadmap <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { value: '1K-10K', label: 'Word count range' },
              { value: '30 days', label: 'Review timeline' },
              { value: '$100-$1K', label: 'Cash rewards' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div className="text-3xl font-extrabold text-brand-700">{stat.value}</div>
                <div className="mt-1 text-sm font-semibold text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Guidelines */}
      <section className="py-24" aria-labelledby="guidelines-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="guidelines-heading" className="section-title">Content Guidelines</h2>
            <p className="section-subtitle mx-auto mt-4">
              Follow these standards to increase your chances of acceptance
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {contentGuidelines.map((guideline) => (
              <div
                key={guideline.title}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={guideline.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{guideline.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{guideline.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accepted Formats */}
      <section className="bg-slate-50 py-24" aria-labelledby="formats-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="formats-heading" className="section-title">Accepted Content Formats</h2>
            <p className="section-subtitle mx-auto mt-4">
              We welcome diverse content types to reach different audiences
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {acceptedFormats.map((format) => (
              <div
                key={format.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={format.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{format.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{format.text}</p>
                <div className="mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {format.formats}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topic Categories */}
      <section className="py-24" aria-labelledby="topics-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="topics-heading" className="section-title">Topics We Cover</h2>
            <p className="section-subtitle mx-auto mt-4">
              Focus your content on these Customer Experience and Support areas
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {topicCategories.map((category) => (
              <div
                key={category.title}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={category.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{category.title}</h3>
                <ul className="mt-3 space-y-1">
                  {category.examples.map((example) => (
                    <li key={example} className="flex items-start gap-2 text-xs text-slate-600">
                      <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-brand-500" />
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review Process */}
      <section className="bg-slate-900 py-24 text-white" aria-labelledby="process-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="process-heading" className="section-title">Review Process</h2>
            <p className="section-subtitle mx-auto mt-4 text-slate-300">
              From submission to publication in 5 steps
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-5">
            {reviewProcess.map((process) => (
              <div
                key={process.step}
                className="relative rounded-2xl border border-slate-800 bg-slate-800/50 p-6 text-center"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-xl font-bold text-white">
                  {process.step}
                </div>
                <h3 className="font-bold text-white">{process.title}</h3>
                <p className="mt-2 text-xs text-slate-300">{process.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-brand-500/30 bg-brand-500/10 p-6 text-center">
            <div className="flex items-center justify-center gap-3">
              <Clock className="h-5 w-5 text-brand-300" />
              <p className="font-semibold text-brand-200">
                Complete review process takes up to <span className="text-white">30 days</span>. Accepted submissions will hear from our content marketing team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section className="py-24" aria-labelledby="rewards-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="rewards-heading" className="section-title">Rewards Program</h2>
            <p className="section-subtitle mx-auto mt-4">
              Choose how you want to be compensated for your contribution
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {rewards.map((reward) => (
              <div
                key={reward.title}
                className="rounded-2xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-500 text-white">
                  <Icon name={reward.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">{reward.title}</h3>
                <p className="mt-4 text-sm text-slate-600">{reward.text}</p>
                <div className="mt-4 inline-flex rounded-full bg-brand-100 px-4 py-1.5 text-sm font-bold text-brand-700">
                  {reward.value}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <div>
                <h4 className="font-bold text-slate-900">Reward Selection</h4>
                <p className="mt-1 text-sm text-slate-600">
                  You can choose between the 2-year Pro subscription or cash payment. Cash rewards range from $100 to $1,000 depending on content quality, depth, research, and practical value. Exceptional content with original data, case studies, and actionable insights receives higher compensation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO & AEO Tips */}
      <section className="bg-slate-50 py-24" aria-labelledby="seo-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="seo-heading" className="section-title">SEO, AEO & GEO Best Practices</h2>
            <p className="section-subtitle mx-auto mt-4">
              Optimize your content for search engines and AI-powered discovery
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {seoTips.map((tip) => (
              <div
                key={tip.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <h3 className="font-bold text-slate-900">{tip.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{tip.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-brand-200 bg-brand-50 p-6">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 flex-shrink-0 text-brand-600" />
              <div>
                <h4 className="font-bold text-slate-900">Why SEO, AEO & GEO Matter</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Search Engine Optimization (SEO) helps your content rank in traditional search. Answer Engine Optimization (AEO) structures content for AI-powered question answering. Generative Engine Optimization (GEO) ensures your content is trusted and referenced by AI models. Together, they maximize visibility and impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submission CTA */}
      <section className="py-24" aria-labelledby="submit-heading">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 id="submit-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to Share Your Expertise?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Send your content to our marketing team for review. Include your title, chosen reward preference, and a brief author bio.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="mailto:marketing@getzurihub.com?subject=Content%20Submission%3A%20%5BYour%20Title%5D&body=Title%3A%20%5BYour%20Article%20Title%5D%0A%0AReward%20Preference%3A%20%5BPro%20Subscription%20or%20Cash%5D%0A%0AAuthor%20Bio%3A%20%5BBrief%20bio%20about%20you%5D%0A%0AContent%20Attachment%3A%20%5BAttach%20your%20file%5D"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-4 text-base font-bold text-white transition hover:bg-brand-700"
            >
              <Send className="h-5 w-5" />
              Submit to marketing@getzurihub.com
            </a>
          </div>
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-left">
            <h4 className="font-bold text-slate-900">Submission Checklist</h4>
            <ul className="mt-3 space-y-2">
              {[
                'Content focuses on CX or Customer Support industry',
                'Word count between 1,000 - 10,000 words',
                'Original, unpublished work',
                'Includes data, examples, or case studies',
                'Well-researched and factually accurate',
                'Free from grammatical errors',
                'Specify your reward preference (Pro subscription or cash)',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-brand-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="faq-heading" className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle mx-auto mt-4">
              Common questions about our content contribution program
            </p>
          </div>
          <div className="mt-10 space-y-4">
            {[
              {
                q: 'What happens if my content is not accepted?',
                a: 'If your content is not accepted, we will provide feedback on how to improve it for future submissions. You are welcome to revise and resubmit.',
              },
              {
                q: 'Can I submit content that has been published elsewhere?',
                a: 'No, we only accept original, unpublished content. If your work has been published on other platforms, please create fresh content for us.',
              },
              {
                q: 'Do I retain ownership of my content?',
                a: 'Yes, you retain ownership. By submitting, you grant ZuriHub a non-exclusive license to publish and promote your content across our channels.',
              },
              {
                q: 'Can I include links to my own website or products?',
                a: 'Yes, you may include one relevant, contextual link to your own website or product. Excessive self-promotion or spammy links will be removed.',
              },
              {
                q: 'How do I choose between Pro subscription and cash reward?',
                a: 'When you submit your content, simply indicate your preference in the email. Our team will honor your choice upon acceptance.',
              },
            ].map((faq) => (
              <div
                key={faq.q}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="font-bold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-900 py-20 text-white" aria-label="Get started">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Start Contributing Today
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            Join our community of CX experts and help shape the future of customer experience.
          </p>
          <a
            href="mailto:marketing@getzurihub.com?subject=Content%20Submission%3A%20%5BYour%20Title%5D"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            <Mail className="h-5 w-5" />
            Submit Your Content
          </a>
        </div>
      </section>
    </>
  );
}
